import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Collapse,
  HStack,
  IconButton,
  Select,
  Spinner,
} from "@chakra-ui/react";
import { FaMinus, FaPlus, FaPlusCircle } from "react-icons/fa";
import { color } from "../../../utils/theme";
import { Option } from "../../../utils/types/props.dropdown";

interface CauseFormProps {
  previousEffect: string;
  previousCauses: any[];
  isViewOnly?: boolean;
  onSubmit: (effect: string, causes: any[]) => void;
  onSaveLocally: (effect: string, causes: any[]) => void;
  formValues: {
    effect: string;
    causes: {
      key: string;
      category: string;
      primaryCauses: {
        primary: string;
        secondaryCauses: string[];
        isSubCauseAdded: boolean;
      }[];
    }[];
  };
}

interface Cause {
  key: string;
  category: string;
  primaryCauses: {
    primary: string;
    secondaryCauses: string[];
    isSubCauseAdded: boolean;
  }[];
}

const initialCauses: Option[] = [
  { label: "People/Manpower", value: "People/Manpower" },
  { label: "Material/Equipment", value: "Material/Equipment" },
  { label: "Processes/Method", value: "Processes/Method" },
  { label: "Environment", value: "Environment" },
  { label: "Other", value: "Other" },
];

const CauseForm: React.FC<CauseFormProps> = ({
  previousEffect,
  previousCauses,
  onSubmit,
  onSaveLocally,
  formValues,
}) => {
  const [effect, setEffect] = useState(
    previousEffect || formValues.effect || ""
  );
  const [causes, setCauses] = useState<Cause[]>(
    previousCauses || formValues.causes || []
  );
  const [isOpen, setIsOpen] = useState<string | null>(null);
  const [newCauseCategory, setNewCauseCategory] = useState<string>("");
  const [isAddingNewCause, setIsAddingNewCause] = useState(false);
  const [enterNewCause, setEnterNewCause] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleToggle = (key: string) => {
    setIsOpen((prev) => (prev === key ? null : key));
  };

  const handleCauseChange = (
    key: string,
    field: string,
    value: string,
    primaryIndex?: number,
    secondaryIndex?: number
  ) => {
    setCauses((prevCauses) =>
      prevCauses.map((cause) =>
        cause.key === key
          ? {
              ...cause,
              primaryCauses: cause.primaryCauses.map((primaryCause, pIndex) =>
                pIndex === primaryIndex
                  ? {
                      ...primaryCause,
                      [field]:
                        field === "secondaryCauses" &&
                        secondaryIndex !== undefined
                          ? primaryCause.secondaryCauses.map((sec, sIndex) =>
                              sIndex === secondaryIndex ? value : sec
                            )
                          : value,
                    }
                  : primaryCause
              ),
            }
          : cause
      )
    );
  };

  const handleAddPrimaryCause = (key: string) => {
    setCauses((prevCauses) =>
      prevCauses.map((cause) =>
        cause.key === key
          ? {
              ...cause,
              primaryCauses: [
                ...cause.primaryCauses,
                { primary: "", secondaryCauses: [], isSubCauseAdded: false },
              ],
            }
          : cause
      )
    );
  };

  const handleAddSecondaryCause = (key: string, primaryIndex: number) => {
    setCauses((prevCauses) =>
      prevCauses.map((cause) =>
        cause.key === key
          ? {
              ...cause,
              primaryCauses: cause.primaryCauses.map((primaryCause, pIndex) =>
                pIndex === primaryIndex
                  ? {
                      ...primaryCause,
                      secondaryCauses: [...primaryCause.secondaryCauses, ""],
                      isSubCauseAdded: true,
                    }
                  : primaryCause
              ),
            }
          : cause
      )
    );
  };

  const handleAddNewCause = () => {
    if (newCauseCategory.trim() === "") {
      alert("Please enter a category for the new cause.");
      return;
    }

    setCauses((prevCauses) => [
      ...prevCauses,
      {
        key: `newCause${prevCauses.length + 1}`,
        category: newCauseCategory,
        primaryCauses: [
          { primary: "", secondaryCauses: [], isSubCauseAdded: false },
        ],
      },
    ]);

    const causes =
      initialCauses.filter((option) => option.value === newCauseCategory)
        .length === 0 && initialCauses;

    setNewCauseCategory("");
    setIsAddingNewCause(false);
    setEnterNewCause(false);
  };

  useEffect(() => {
    if (
      previousEffect ||
      previousCauses.length ||
      formValues.effect ||
      formValues.causes
    ) {
      setLoading(true);
      setTimeout(() => {
        const initialEffect = previousEffect || formValues.effect || "";
        const initialCauses = previousCauses.length
          ? previousCauses
          : formValues.causes || [];

        setEffect(initialEffect);
        setCauses(initialCauses);

        // Save locally only once during initialization
        if (!loading) {
          onSaveLocally(initialEffect, initialCauses);
        }

        setLoading(false);
      }, 1000);
    }
  }, []); // No dependencies, runs only once

  const handleSubmit = () => {
    onSubmit(effect, causes);
  };

  return (
    <Box>
      {loading ? (
        <Spinner />
      ) : (
        <>
          <FormControl id="effect" isRequired>
            <FormLabel>Effect</FormLabel>
            <Input
              value={effect}
              onChange={(e) => setEffect(e.target.value)}
              onBlur={() => onSaveLocally(effect, causes)}
              placeholder="Enter the effect"
            />
          </FormControl>

          {causes.map((cause) => (
            <Box key={cause.key} mt={4}>
              <HStack
                justifyContent="space-between"
                bgColor={color.primary}
                p={2}
                alignItems="center"
                borderTopRadius={5}
                shadow="md"
              >
                <FormLabel color={color.secondary}>{cause.category}</FormLabel>
                <IconButton
                  aria-label="Toggle Collapse"
                  icon={isOpen === cause.key ? <FaMinus /> : <FaPlus />}
                  onClick={() => handleToggle(cause.key)}
                />
              </HStack>
              <Collapse in={isOpen === cause.key}>
                <VStack
                  spacing={4}
                  borderBottomRadius={5}
                  borderWidth={1}
                  align="flex-start"
                  borderColor={color.primary}
                  p={5}
                >
                  {cause.primaryCauses.map((primaryCause, primaryIndex) => (
                    <Box key={primaryIndex} w="100%">
                      <FormControl
                        id={`${cause.key}-primary-${primaryIndex}`}
                        isRequired
                      >
                        <FormLabel>Cause</FormLabel>
                        <Input
                          value={primaryCause.primary}
                          onChange={(e) =>
                            handleCauseChange(
                              cause.key,
                              "primary",
                              e.target.value,
                              primaryIndex
                            )
                          }
                          onBlur={() => onSaveLocally(effect, causes)}
                          placeholder="Enter the Cause"
                        />
                      </FormControl>
                      {primaryCause.secondaryCauses.map(
                        (secondaryCause, secondaryIndex) => (
                          <FormControl
                            key={secondaryIndex}
                            id={`${cause.key}-primary-${primaryIndex}-secondary-${secondaryIndex}`}
                            mt={2}
                          >
                            <FormLabel fontSize="md">Sub Cause</FormLabel>
                            <Input
                              value={secondaryCause}
                              onChange={(e) =>
                                handleCauseChange(
                                  cause.key,
                                  "secondaryCauses",
                                  e.target.value,
                                  primaryIndex,
                                  secondaryIndex
                                )
                              }
                              onBlur={() => onSaveLocally(effect, causes)}
                              placeholder="Enter the secondary cause (optional)"
                            />
                          </FormControl>
                        )
                      )}
                      <Button
                        onClick={() =>
                          handleAddSecondaryCause(cause.key, primaryIndex)
                        }
                        mt={2}
                        variant="outline"
                        w="100%"
                        colorScheme="blue"
                        fontWeight="normal"
                        leftIcon={<FaPlusCircle />}
                      >
                        Add Sub Cause
                      </Button>
                    </Box>
                  ))}
                  <Button
                    onClick={() => handleAddPrimaryCause(cause.key)}
                    mt={2}
                    ml="auto"
                    colorScheme="blue"
                    fontSize="sm"
                    variant="outline"
                    leftIcon={<FaPlusCircle />}
                  >
                    Add Cause
                  </Button>
                </VStack>
              </Collapse>
            </Box>
          ))}

          {!isAddingNewCause ? (
            <Button
              onClick={() => setIsAddingNewCause(true)}
              mt={4}
              w="100%"
              colorScheme="blue"
              fontSize="sm"
              leftIcon={<FaPlus />}
            >
              Add New Cause
            </Button>
          ) : (
            <VStack mt={4} width="100%" align="flex-start">
              <FormLabel>Main Cause</FormLabel>
              <HStack w="100%">
                <FormControl isRequired>
                  <Select
                    placeholder="Select Cause"
                    onChange={(e) => {
                      const value = e.target.value;
                      if (value === "Other") {
                        setEnterNewCause(true);
                      } else {
                        setEnterNewCause(false);
                        setNewCauseCategory(value);
                      }
                    }}
                  >
                    {initialCauses.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {enterNewCause && (
                    <Input
                      value={newCauseCategory}
                      onChange={(e) => setNewCauseCategory(e.target.value)}
                      placeholder="Enter main cause"
                      mt={2}
                    />
                  )}
                </FormControl>
                <Button
                  onClick={handleAddNewCause}
                  isDisabled={!newCauseCategory.trim()}
                  colorScheme="blue"
                  fontSize="sm"
                  leftIcon={<FaPlus />}
                >
                  Add
                </Button>
              </HStack>
            </VStack>
          )}

          {/* <Button onClick={handleSubmit} mt={4} colorScheme="green" w="100%">
            Submit
          </Button> */}
        </>
      )}
    </Box>
  );
};

export default CauseForm;
